import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { confirmDialog } from 'primereact/confirmdialog';
import DealerDropdown from '../../../../../components/common/DealerDropdown';
import GenericFileUpload from '../../../../../components/common/GenericFileUpload';
import { formatDateWithTime } from '../../../../../utils/dateUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const VehicleWarrantyPeriodForm = ({ visible, onHide, onSubmit, initialData, documents = [], isLoadingDocuments = false, onDownloadDocument, itemLoading = false }) => {
    const toast = useRef(null);
    const { th } = useLanguage();
    const [newFiles, setNewFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [initialFormData, setInitialFormData] = useState(null);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = React.useState({
        id: 0,
        addWarrantyPeriod: 0,
        addWarrantyKmInfo: 0,
        curWarrantyPeriod: 0,
        curWarrantyKmInfo: 0,
        odometerKmInfo: 0,
        addWarrDetails: '',
        dealerId: 0,
        endUser: '',
        sellDate: null,
        bid: 0,
        bidWarrantyPeriod: null,
        bidWarrantyKmInfo: null
    });

    React.useEffect(() => {
        const defaultFormData = {
            id: 0,
            addWarrantyPeriod: 0,
            addWarrantyKmInfo: 0,
            curWarrantyPeriod: 0,
            curWarrantyKmInfo: 0,
            odometerKmInfo: 0,
            addWarrDetails: '',
            dealerId: 0,
            endUser: '',
            sellDate: null,
            bid: 0,
            bidWarrantyPeriod: null,
            bidWarrantyKmInfo: null
        };

        if (initialData) {
            const parsedSellDate = initialData.sellDate && typeof initialData.sellDate === 'string' ? new Date(initialData.sellDate) : initialData.sellDate;
            const validSellDate = parsedSellDate instanceof Date && !isNaN(parsedSellDate) ? parsedSellDate : null;

            const newFormData = {
                ...defaultFormData,
                ...initialData,
                sellDate: validSellDate,
                addWarrantyPeriod: initialData.addWarrantyPeriod || 0,
                addWarrantyKmInfo: initialData.addWarrantyKmInfo || 0,
                curWarrantyPeriod: initialData.curWarrantyPeriod || 0,
                curWarrantyKmInfo: initialData.curWarrantyKmInfo || 0,
                odometerKmInfo: initialData.odometerKmInfo || 0,
                dealerId: initialData.dealerId || 0,
                bid: initialData.bid || 0,
                bidWarrantyPeriod: initialData.bidWarrantyPeriod || null,
                bidWarrantyKmInfo: initialData.bidWarrantyKmInfo || null
            };

            setFormData(newFormData);
            setInitialFormData(newFormData);
        } else {
            setFormData(defaultFormData);
            setInitialFormData(defaultFormData);
        }
        if (visible) {
            setNewFiles([]);
            setErrors({});
        }
    }, [initialData, visible]);

    const checkFormChanged = () => {
        if (!initialFormData) return false;

        const fieldsToCheck = ['addWarrantyPeriod', 'addWarrantyKmInfo', 'curWarrantyPeriod', 'curWarrantyKmInfo', 'odometerKmInfo', 'addWarrDetails', 'dealerId', 'endUser', 'sellDate', 'bid', 'bidWarrantyPeriod', 'bidWarrantyKmInfo'];

        for (const field of fieldsToCheck) {
            if (field === 'sellDate') {
                const initial = initialFormData[field];
                const current = formData[field];
                if ((initial?.getTime() || null) !== (current?.getTime() || null)) {
                    return true;
                }
            } else if (formData[field] !== initialFormData[field]) {
                return true;
            }
        }

        return newFiles.length > 0;
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.endUser || formData.endUser.trim() === '') {
            newErrors.endUser = th('vehicleAdditionalWarrantyPeriod.validation.endUserRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const confirmFileUpdate = () => {
        return new Promise((resolve) => {
            confirmDialog({
                message: th('vehicleAdditionalWarrantyPeriod.fileUpdateConfirmMessage'),
                header: th('vehicleAdditionalWarrantyPeriod.fileUpdateConfirmHeader'),
                icon: 'pi pi-exclamation-triangle',
                acceptClassName: 'p-button-danger',
                rejectClassName: 'p-button-text',
                acceptLabel: th('common.yes'),
                rejectLabel: th('common.cancel'),
                accept: () => resolve(true),
                reject: () => resolve(false)
            });
        });
    };

    const confirmExit = () => {
        return new Promise((resolve) => {
            confirmDialog({
                message: th('vehicleAdditionalWarrantyPeriod.exitConfirmMessage'),
                header: th('vehicleAdditionalWarrantyPeriod.exitConfirmHeader'),
                icon: 'pi pi-exclamation-triangle',
                acceptClassName: 'p-button-danger',
                rejectClassName: 'p-button-text',
                acceptLabel: th('common.yes'),
                rejectLabel: th('common.cancel'),
                accept: () => resolve(true),
                reject: () => resolve(false)
            });
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('vehicleAdditionalWarrantyPeriod.validation.fillRequiredFields'),
                life: 3000
            });
            return;
        }

        if (newFiles.length > 0 && documents.length > 0) {
            const confirmed = await confirmFileUpdate();
            if (!confirmed) {
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const hasFormChanged = checkFormChanged();
            await onSubmit({ ...formData, files: newFiles }, hasFormChanged);
        } catch (error) {
            console.error('Submit işlemi sırasında hata oluştu:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (field, value) => {
        let updatedData = {
            ...formData,
            [field]: value
        };
        if (field === 'bid' && value === 0) {
            updatedData = {
                ...updatedData,
                addWarrDetails: '',
                bidWarrantyPeriod: null,
                bidWarrantyKmInfo: null
            };
        }

        setFormData(updatedData);

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const handleDealerChange = (dealer) => {
        setFormData((prev) => ({
            ...prev,
            dealerId: dealer?.id || 0
        }));
    };

    const handleSafeDownload = async (doc, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (onDownloadDocument) {
            setIsDownloading(true);
            try {
                await onDownloadDocument(doc);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Başarılı',
                    detail: 'Dosya başarıyla indirildi',
                    life: 3000
                });
            } catch (error) {
                console.error('Error downloading document:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: th('vehicleAdditionalWarrantyPeriod.downloadError'),
                    detail: th('vehicleAdditionalWarrantyPeriod.downloadErrorMessage'),
                    life: 3000
                });
            } finally {
                setIsDownloading(false);
            }
        }
    };

    const handleHide = async () => {
        const hasChanges = checkFormChanged();
        if (hasChanges) {
            const confirmed = await confirmExit();
            if (!confirmed) {
                return;
            }
        }
        onHide();
    };

    const renderFooter = () => {
        const isFormDisabled = isSubmitting || isDownloading;

        return (
            <div className="flex justify-content-end" style={{ marginRight: '10px' }}>
                <Button label={th('warrantyClaimLimit.buttons.cancel')} icon="pi pi-times" outlined onClick={handleHide} disabled={isFormDisabled} />
                <Button label={th('warrantyClaimLimit.buttons.save')} icon="pi pi-check" autoFocus onClick={handleSubmit} loading={isSubmitting} disabled={isDownloading} />
            </div>
        );
    };

    const dialogHeader = (
        <div className="flex align-items-center justify-content-between">
            <span className="text-xl font-semibold">{th('vehicleAdditionalWarrantyPeriod.titleWarrantyPeriod')}</span>
            {(isSubmitting || isDownloading) && (
                <div className="flex align-items-center gap-2">
                    <ProgressSpinner style={{ width: '20px', height: '20px' }} />
                    <span className="text-sm">{isSubmitting ? th('vehicleAdditionalWarrantyPeriod.saving') : th('vehicleAdditionalWarrantyPeriod.downloading')}</span>
                </div>
            )}
        </div>
    );

    const fileUploadMessage = documents.length > 0 ? th('vehicleAdditionalWarrantyPeriod.fileUploadMessage') : null;

    const isFormDisabled = isSubmitting || isDownloading;

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <Dialog
                visible={visible}
                style={{ width: '90%', maxWidth: '1300px' }}
                header={dialogHeader}
                modal
                className="p-fluid warranty-dialog"
                footer={renderFooter()}
                onHide={handleHide}
                maximizable
                maximized
                blockScroll
                closable={!isFormDisabled}
            >
                {itemLoading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <>
                        {isSubmitting && <Message severity="info" text={th('vehicleAdditionalWarrantyPeriod.processingData')} className="w-full mb-4" icon="pi pi-spin pi-spinner" />}

                        {isDownloading && <Message severity="info" text={th('vehicleAdditionalWarrantyPeriod.downloadingFile')} className="w-full mb-4" icon="pi pi-spin pi-spinner" />}

                        {newFiles.length > 0 && documents.length > 0 && <Message severity="warn" text={fileUploadMessage} className="w-full mb-4" icon="pi pi-exclamation-triangle" />}

                        <div className={isFormDisabled ? 'p-disabled' : ''}>
                            <h3 className="section-title">
                                <i className="pi pi-car mr-2"></i>
                                {th('vehicleAdditionalWarrantyPeriod.sections.vehicleInfo')}
                            </h3>
                            <Divider />

                            <div className="grid">
                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="chassisNo" className="font-bold block mb-2">
                                            <i className="pi pi-hashtag mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.columns.chassisNo')}
                                        </label>
                                        <InputText id="chassisNo" value={initialData?.chassisNo || ''} disabled className="w-full" />
                                    </div>
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="carManuDate" className="font-bold block mb-2">
                                            <i className="pi pi-calendar mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.columns.carManuDate')}
                                        </label>
                                        <InputText id="carManuDate" value={formatDateWithTime(initialData?.carManuDate) || ''} disabled className="w-full" />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <h3 className="section-title mt-1">
                                        <i className="pi pi-users mr-2"></i>
                                        {th('vehicleAdditionalWarrantyPeriod.sections.customerInfo')}
                                    </h3>
                                    <Divider />
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="endUser" className="font-bold block mb-2">
                                            <i className="pi pi-user mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.formLabels.endUser')} *
                                        </label>
                                        <InputText
                                            id="endUser"
                                            value={formData.endUser}
                                            onChange={(e) => handleInputChange('endUser', e.target.value)}
                                            placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.endUser')}
                                            className={errors.endUser ? 'p-invalid w-full' : 'w-full'}
                                            disabled={isFormDisabled}
                                        />
                                        {errors.endUser && (
                                            <small className="p-error">
                                                <i className="pi pi-exclamation-circle mr-1"></i>
                                                {errors.endUser}
                                            </small>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="sellDate" className="font-bold block mb-2">
                                            <i className="pi pi-calendar mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.formLabels.sellDate')}
                                        </label>
                                        <Calendar
                                            id="sellDate"
                                            value={formData.sellDate}
                                            onChange={(e) => handleInputChange('sellDate', e.value)}
                                            dateFormat="dd/mm/yy"
                                            showIcon
                                            placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.sellDate')}
                                            className="w-full"
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="dealerId" className="font-bold block mb-2">
                                            <i className="pi pi-shop mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.formLabels.dealer')}
                                        </label>
                                        <DealerDropdown value={formData.dealerId} onChange={handleDealerChange} className="w-full" disabled={isFormDisabled} />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <h3 className="section-title mt-1">
                                        <i className="pi pi-shield mr-2"></i>
                                        {th('vehicleAdditionalWarrantyPeriod.sections.warrantyInfo')}
                                    </h3>
                                    <Divider />
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="addWarrantyPeriod" className="font-bold block mb-2">
                                            <i className="pi pi-clock mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.formLabels.addWarrantyPeriod')}
                                        </label>
                                        <InputNumber
                                            id="addWarrantyPeriod"
                                            value={formData.addWarrantyPeriod}
                                            onValueChange={(e) => handleInputChange('addWarrantyPeriod', e.value)}
                                            mode="decimal"
                                            minFractionDigits={0}
                                            maxFractionDigits={0}
                                            className="w-full"
                                            placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.period')}
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="addWarrantyKmInfo" className="font-bold block mb-2">
                                            <i className="pi pi-map-marker mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.formLabels.addWarrantyKmInfo')}
                                        </label>
                                        <InputNumber
                                            id="addWarrantyKmInfo"
                                            value={formData.addWarrantyKmInfo}
                                            onValueChange={(e) => handleInputChange('addWarrantyKmInfo', e.value)}
                                            mode="decimal"
                                            minFractionDigits={0}
                                            maxFractionDigits={0}
                                            className="w-full"
                                            placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.kmInfo')}
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="curWarrantyPeriod" className="font-bold block mb-2">
                                            <i className="pi pi-calendar-clock mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.formLabels.curWarrantyPeriod')}
                                        </label>
                                        <InputNumber
                                            id="curWarrantyPeriod"
                                            value={formData.curWarrantyPeriod}
                                            onValueChange={(e) => handleInputChange('curWarrantyPeriod', e.value)}
                                            mode="decimal"
                                            minFractionDigits={0}
                                            maxFractionDigits={0}
                                            className="w-full"
                                            placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.currentPeriod')}
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="curWarrantyKmInfo" className="font-bold block mb-2">
                                            <i className="pi pi-compass mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.formLabels.curWarrantyKmInfo')}
                                        </label>
                                        <InputNumber
                                            id="curWarrantyKmInfo"
                                            value={formData.curWarrantyKmInfo}
                                            onValueChange={(e) => handleInputChange('curWarrantyKmInfo', e.value)}
                                            mode="decimal"
                                            minFractionDigits={0}
                                            maxFractionDigits={0}
                                            className="w-full"
                                            placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.currentKm')}
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field mb-0">
                                        <label htmlFor="odometerKmInfo" className="font-bold block mb-2">
                                            <i className="pi pi-gauge mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.formLabels.odometerKmInfo')}
                                        </label>
                                        <InputNumber
                                            id="odometerKmInfo"
                                            value={formData.odometerKmInfo}
                                            onValueChange={(e) => handleInputChange('odometerKmInfo', e.value)}
                                            mode="decimal"
                                            minFractionDigits={0}
                                            maxFractionDigits={0}
                                            className="w-full"
                                            placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.odometerKm')}
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <h3 className="section-title mt-1">
                                        <i className="pi pi-bookmark mr-2"></i>
                                        {th('vehicleAdditionalWarrantyPeriod.sections.bidInfo')}
                                    </h3>
                                    <Divider />
                                </div>

                                <div className="col-12 md:col-3">
                                    <div className="field mb-0">
                                        <label className="font-bold block mb-2">
                                            <i className="pi pi-question-circle mr-1"></i>
                                            {th('vehicleAdditionalWarrantyPeriod.formLabels.bidExists')}
                                        </label>
                                        <div className="flex gap-3">
                                            <div className="flex align-items-center">
                                                <RadioButton inputId="bidYes" name="bid" value={1} onChange={(e) => handleInputChange('bid', e.value)} checked={formData.bid === 1} disabled={isFormDisabled} />
                                                <label htmlFor="bidYes" className="ml-2">
                                                    {th('common.yes')}
                                                </label>
                                            </div>
                                            <div className="flex align-items-center">
                                                <RadioButton inputId="bidNo" name="bid" value={0} onChange={(e) => handleInputChange('bid', e.value)} checked={formData.bid === 0} disabled={isFormDisabled} />
                                                <label htmlFor="bidNo" className="ml-2">
                                                    {th('common.no')}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {formData.bid === 1 && (
                                    <>
                                        <div className="col-12 md:col-3">
                                            <div className="field mb-0">
                                                <label htmlFor="bidWarrantyPeriod" className="font-bold block mb-2">
                                                    <i className="pi pi-clock mr-1"></i>
                                                    {th('vehicleAdditionalWarrantyPeriod.formLabels.bidWarrantyPeriod')}
                                                </label>
                                                <InputNumber
                                                    id="bidWarrantyPeriod"
                                                    value={formData.bidWarrantyPeriod}
                                                    onValueChange={(e) => handleInputChange('bidWarrantyPeriod', e.value)}
                                                    mode="decimal"
                                                    minFractionDigits={0}
                                                    maxFractionDigits={0}
                                                    placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.period')}
                                                    className="w-full"
                                                    disabled={isFormDisabled}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-3">
                                            <div className="field mb-0">
                                                <label htmlFor="bidWarrantyKmInfo" className="font-bold block mb-2">
                                                    <i className="pi pi-map-marker mr-1"></i>
                                                    {th('vehicleAdditionalWarrantyPeriod.formLabels.bidWarrantyKmInfo')}
                                                </label>
                                                <InputNumber
                                                    id="bidWarrantyKmInfo"
                                                    value={formData.bidWarrantyKmInfo}
                                                    onValueChange={(e) => handleInputChange('bidWarrantyKmInfo', e.value)}
                                                    mode="decimal"
                                                    minFractionDigits={0}
                                                    maxFractionDigits={0}
                                                    placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.kmInfo')}
                                                    className="w-full"
                                                    disabled={isFormDisabled}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-3">
                                            <div className="field mb-0">
                                                <label htmlFor="addWarrDetails" className="font-bold block mb-2">
                                                    <i className="pi pi-file-text mr-1"></i>
                                                    {th('vehicleAdditionalWarrantyPeriod.formLabels.description')}
                                                </label>
                                                <InputTextarea
                                                    id="addWarrDetails"
                                                    value={formData.addWarrDetails}
                                                    onChange={(e) => handleInputChange('addWarrDetails', e.target.value)}
                                                    rows={1}
                                                    placeholder={th('vehicleAdditionalWarrantyPeriod.placeholders.description')}
                                                    className="w-full"
                                                    disabled={isFormDisabled}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="col-12">
                                    <h3 className="section-title mt-1">
                                        <i className="pi pi-paperclip mr-2"></i>
                                        {th('vehicleAdditionalWarrantyPeriod.sections.fileUpload')}
                                    </h3>
                                    <Divider />

                                    <GenericFileUpload
                                        newFiles={newFiles}
                                        onFilesChange={setNewFiles}
                                        existingDocuments={documents}
                                        onDownloadDocument={handleSafeDownload}
                                        onDeleteExistingDocument={undefined}
                                        onUpdateExistingDocument={undefined}
                                        isLoading={isLoadingDocuments}
                                        multiple={false}
                                        showDownloadButton={true}
                                        showUpdateButton={false}
                                        showDeleteButton={false}
                                        disabled={isFormDisabled}
                                        maxFileSize={20971520} // 20MB
                                        acceptedFileTypes={{
                                            'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
                                            'application/pdf': ['.pdf'],
                                            'application/msword': ['.doc'],
                                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                                            'application/vnd.ms-excel': ['.xls'],
                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                            'text/plain': ['.txt']
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default VehicleWarrantyPeriodForm;
